import { Autocomplete } from "@material-ui/lab";
import React, { ReactElement, useState, useEffect } from "react";
import cx from "classnames";
import styles from "../scss/AFDisclosure.module.scss";
import ValidationMessage from "../../common/components/Forms/ValidationMessage/ValidationMessage";
import useIsMounted from "../../common/hooks/useIsMounted";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import { getDisclosures } from "../api/ApiAppForm";
import { makeStyles } from "@material-ui/core";
import { useParams } from "react-router-dom";
import Icon from "../../common/components/Icon/Icon";
import { IAnswerDisclosureProps, IDisclosureProps } from "../interfaces/IAppFormData";
import { IDisclosureOptionProps, UNKNOWN, UNKNOWN_OPTION } from "./AFDisclosure";
import { decodeHtml } from "../shared/AppFormHelper";
import { useAppFormUtils } from "../hooks/useAppFormUtils";

type AppFormParams = {
    applicantToken: string;
};

interface IAFMultiDisclosureProps {
    id?: string;
    title?: string;
    placeholder?: string;

    parameterType: string;
    initialValues: Array<IDisclosureOptionProps>;

    onSelect?: (options: IAnswerDisclosureProps[]) => void;
    onBlur?: (e: React.ChangeEvent<HTMLDivElement>) => void;

    className?: string;
    hasError?: string | boolean;

    hasCantFindOption: boolean;
}

const useStyles = (hasCantFind: boolean) =>
    hasCantFind ?
        makeStyles(() => ({
            listbox: {
                "paddingBottom": 0,
                "& > * > div": {
                    display: "flex",
                    justifyContent: "space-between"
                },
                "& > *:last-child": {
                    backgroundColor: "#ffc0cb"
                }
            }
        }))
    : makeStyles(() => ({
        listbox: {
            "paddingBottom": 0,
            "& > * > div": {
                display: "flex",
                justifyContent: "space-between"
            }
        }
    }));

const AFMultiDisclosure: React.FC<IAFMultiDisclosureProps> = (props): ReactElement => {
    const {
        id,
        title,
        parameterType,
        initialValues,
        placeholder,
        onSelect,
        onBlur,
        className,
        children,
        hasError,
        hasCantFindOption
    } = props;

    const classes = useStyles(hasCantFindOption)();

    const ErrorLogger = useErrorLogger();

    const { checkSearchTerm } = useAppFormUtils();

    const [occupationOptions, setOccupationOptions] = useState<IDisclosureOptionProps[]>(
        []
    );
    const [isLoading, setIsLoading] = useState(false);
    const isMounted = useIsMounted();
    const params = useParams<AppFormParams>();
    const applicantToken = params.applicantToken;
    const [showClearBtn, setShowClearBtn] = useState(false);
    const [values, setValues] = useState<IDisclosureOptionProps[]>([]);
    const [searchTimeout, setSearchTimeout] = useState<any>(null);

    const handleFindOptions = (queryString: string): void => {
        setIsLoading(true);
        const params: IDisclosureProps = {
            applicantToken,
            questionParameter: parameterType,
            search: queryString
        };
        getDisclosures(params)
            .then((disclosures) => {
                const optionList = disclosures || [];
                if (hasCantFindOption) {
                    setOccupationOptions([
                        ...optionList.map((i) => ({
                            label: decodeHtml(i.disclosure),
                            value: i.disclosureId
                        })),
                        UNKNOWN_OPTION
                    ]);
                } else {
                    setOccupationOptions([
                        ...optionList.map((i) => ({
                            label: decodeHtml(i.disclosure),
                            value: i.disclosureId
                        }))
                    ]);
                }
            })
            .catch((err) => {
                ErrorLogger.Log(err, {
                    source: "Occupation"
                });
                setOccupationOptions([]);
            })
            .finally(() => {
                isMounted.current && setIsLoading(false);
            });
    };

    const handleFind = (queryString: string): void => {
        setIsLoading(true);
        clearTimeout(searchTimeout);

        const timeout = setTimeout(() => {
            handleFindOptions(queryString);
        }, 500);

        setSearchTimeout(timeout);
    };

    const handleCantFindOption = (queryString): void => {
        onSelect([
            {
                disclosure: queryString || "",
                disclosureId: "-1",
                selected: true,
                sortOrder: 0
            }
        ]);
    };

    const handleClearField = (): void => {
        setShowClearBtn(false);
        setValues([]);
        onSelect([]);
        handleFindOptions("");
    };

    const handleRemoveSelected = (value: string): void => {
        const newList = values.filter((i) => i.value !== value);
        setShowClearBtn(!!newList?.length);
        setValues(newList);
        handleSelect(newList);
    };

    const handleChange = (evt, val): void => {
        if (!evt) {
            return;
        }
        if (val?.length < values?.length) {
            handleClearField();
            return;
        }
        const isCantFindOption = !!val.find((i) => i.value === "-1")?.value || false;
        setShowClearBtn(!!val?.length);
        if (isCantFindOption && val?.length > 1) {
            setValues(val.filter((i) => i.value !== "-1"));
            handleSelect(val.filter((i) => i.value !== "-1"));
            return;
        }
        setValues(val);
        handleSelect(val);
    };

    const handleSelect = (values): void => {
        onSelect(
            values.map((item, idx) => ({
                disclosure: item.label,
                disclosureId: item.value,
                selected: true,
                sortOrder: idx + 1
            }))
        );
    };

    const handleFilterOption = (opt) => {
        if (hasCantFindOption) {
            return values?.length
                ? [...opt.filter((item) => item.label !== UNKNOWN)]
                : [...opt.filter((item) => item.label !== UNKNOWN), UNKNOWN_OPTION];
        }
        return [...opt.filter((item) => item.label !== UNKNOWN)];
    };

    const handleInitialValues = (list): void => {
        const listValue = list.reduce((acc, item) => {
            return [
                ...acc,
                {
                    label: item.disclosure,
                    value: item.disclosureId
                }
            ];
        }, []);
        setValues(listValue);
        handleSelect(listValue);
        setShowClearBtn(true);
    };

    useEffect(() => {
        if (initialValues?.length) {
            handleInitialValues(initialValues);
        }
        return () => {
            clearTimeout(searchTimeout);
        };
    }, []);

    return (
        <div
            className={cx(styles["input-container"], styles["occupation"], "gap-m", {
                [className]: !!className
            })}>
            <p
                className={cx(
                    { [styles["inputwrapper--error-title"]]: hasError },
                    "text-bold",
                    "text-font-size-s",
                    "text-zero-margin"
                )}>
                {title || "Occupation"}
            </p>
            <p className="text-help">Select as many as you need.</p>
            <div
                className={cx(
                    styles["inputwrapper"],
                    styles["inputwrapper--lightgreen"]
                )}>
                <Autocomplete
                    id={id}
                    classes={classes}
                    options={[...occupationOptions, UNKNOWN_OPTION]}
                    multiple={true}
                    value={values}
                    getOptionLabel={(option) => option.label}
                    onInputChange={(e, val) => {
                        if (!e) {
                            return;
                        }
                        if (val && val !== UNKNOWN && checkSearchTerm(val)) {
                            handleFind(val);
                        } else if (val === UNKNOWN) {
                            handleCantFindOption(val);
                        }

                        const existingOption = occupationOptions?.find(
                            (i) => i.label === val
                        );

                        if (val !== UNKNOWN) {
                            onSelect([
                                {
                                    disclosure: val || "",
                                    disclosureId: existingOption?.value || "",
                                    selected: true,
                                    sortOrder: 0
                                }
                            ]);
                        }
                    }}
                    onChange={handleChange}
                    onBlur={(event) => {
                        onBlur(event);
                    }}
                    getOptionSelected={(option, value) => {
                        return option.label === value.label || option.label === UNKNOWN;
                    }}
                    filterOptions={handleFilterOption}
                    filterSelectedOptions
                    renderInput={(params) => (
                        <div
                            ref={params.InputProps.ref}
                            className={styles["af-disclosure-wrapper"]}>
                            <input
                                type={"text"}
                                autoFocus
                                placeholder={placeholder}
                                {...params.inputProps}
                                className={cx({
                                    [styles["inputwrapper-textinput"]]: true,
                                    [styles["inputwrapper--error"]]: hasError
                                })}
                            />
                            {showClearBtn && (
                                <button
                                    type="button"
                                    className={styles["af-clear-icon"]}
                                    onClick={handleClearField}>
                                    Clear
                                </button>
                            )}
                        </div>
                    )}
                    fullWidth
                    autoHighlight
                    selectOnFocus
                    loading={isLoading}
                />
            </div>

            {showClearBtn && (
                <div className={styles["af-disclosure-selected"]}>
                    {values.map((item, idx) => (
                        <div className={styles["af-disclosure-selected-item"]} key={idx}>
                            {item.label}
                            <button
                                type="button"
                                className={cx(styles["action-icon"])}
                                onClick={() => {
                                    handleRemoveSelected(item.value);
                                }}>
                                <Icon size="ml" id="RedCross" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {hasError && typeof hasError === "string" && (
                <ValidationMessage>{hasError}</ValidationMessage>
            )}
            {children}
        </div>
    );
};

export default AFMultiDisclosure;
